import { useRef, useState, useEffect } from "react";

export default function AccessForm({ localization, accessFormState, state, language, onSubmit }) {
  const [userFile, setUserFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const dragCounter = useRef(0);
  const formRef = useRef(null);

  useEffect(() => {
    if (!state) return;

    const handleClickOutside = (event) => {
      if (formRef.current && !formRef.current.contains(event.target)) {
        accessFormState();
        setError("");
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") accessFormState();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [state, accessFormState]);

  const handleFile = (file) => {
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".data")) {
      setError(`${localization.fileCheckError[language]}`);
      setUserFile(null);
      setNotice("");
      return;
    }

    setError("");
    setNotice("");
    setUserFile(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!userFile) return;

    const isCreated = await onSubmit(userFile);

    if (isCreated) {
      setNotice(localization.fileReady[language]);
      setError("");
    }
  };

  const dragEvents = (dragType) => (event) => {
    // тут реализация Перетаскивания файла, без валидации
    event.preventDefault();

    if (dragType === "drag") {
      if (!event.dataTransfer.types.includes("Files")) return;

      dragCounter.current += 1;
      setIsDragging(true);
    }

    if (dragType === "over") {
      if (!event.dataTransfer.types.includes("Files")) return;

      event.dataTransfer.dropEffect = "copy";
    }

    if (dragType === "leave") {
      dragCounter.current = Math.max(0, dragCounter.current - 1);

      if (dragCounter.current === 0) {
        setIsDragging(false);
      }
    }

    if (dragType === "drop") {
      dragCounter.current = 0;
      setIsDragging(false);

      const file = event.dataTransfer.files?.[0] || null;

      handleFile(file);
    }
  };

  return (
    <div
      ref={formRef}
      className={`access-form ${state ? "access-form--open" : ""} flex-column`}
      role="dialog"
      aria-modal="true"
      aria-label={localization.createNewAccess[language]}
      onDragEnter={dragEvents("drag")}
      onDragOver={dragEvents("over")}
      onDragLeave={dragEvents("leave")}
      onDrop={dragEvents("drop")}
    >
      <form className="access-form__form flex-column" onSubmit={handleSubmit}>
        <label htmlFor="userFile" className={`access-form__label flex-row ${isDragging ? "access-form__label--dragging" : ""}`}>
          <span className="access-form__placeholder">
            {isDragging ? localization.dropHere[language] : userFile ? userFile.name : localization.fileInput[language]}
          </span>

          <input
            id="userFile"
            type="file"
            accept=".data"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0] || null;
              handleFile(file);
            }}
          />
        </label>

        {error && <div className="access-form__error">{localization.fileCheckError[language]}</div>}
        {notice && (
          <div className="access-form__notice" role="status">
            {notice}
          </div>
        )}

        <div className="access-form__actions flex-row">
          <button className="access-button form-button button uppercase" type="submit" disabled={!userFile || error ? true : false}>
            {localization.create[language]}
          </button>

          <button
            className="form-button button uppercase"
            type="button"
            onClick={() => {
              accessFormState();
              setError("");
            }}
          >
            {localization.cancel[language]}
          </button>
        </div>
      </form>
    </div>
  );
}
